import { Heading } from '@rhg/components/heading';
import { maxWidths } from '@rhg/components/ui/layout';
import { Box, CircularProgress, Stack, Typography } from '@rhg/libs/material';
import { CheckmarkCircleOutline } from '@rhg/libs/material/icons';
import { sxAppend } from '@rhg/libs/material/sx';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { LoadingDialogContentProps } from '../types';
import generationStepMessages from './messages';

const LoadingDots = dynamic(() =>
    import('@rhg/components/animations/loading-dots').then(
        (mod) => mod.LoadingDots
    )
);

const SuccessCheckAnimation = dynamic(() =>
    import('@rhg/components/animations/success-check-animation').then(
        (mod) => mod.SuccessCheckAnimation
    )
);

/**
 * How long the heading holds on its own before the first generation step joins
 * it. Shorter than the interval between steps so the list starts building while
 * the heading is still landing rather than leaving it alone on screen.
 */
export const FIRST_GENERATION_STEP_DELAY_MS = 2000;

/** How long each revealed step waits before the next one appears. */
export const GENERATION_STEP_INTERVAL_MS = 5000;

/**
 * How long the last step keeps working after it appears, so it doesn't arrive
 * already checked off. Shorter than a full interval — by then the list is done
 * building and the hold is only there to let the last step read as work.
 */
export const LAST_GENERATION_STEP_HOLD_MS = 3000;

/**
 * How long the finished list — every step checked off, the success animation
 * playing — stays on screen before the loader hands back and the screen behind
 * it takes over. Without it the check lands on the same frame as the
 * transition, so the sequence reads as ending mid-work.
 */
export const GENERATION_COMPLETE_HOLD_MS = 2000;

/**
 * The generation steps, in reveal order. The reveal is additive — a step never
 * replaces the heading or the step before it — so the loader reads as a list
 * building up: heading alone at 0s, and all four steps on screen by 17s.
 */
export const GENERATION_STEPS = [
    generationStepMessages['loadingDialog.generationStep.analyzing'],
    generationStepMessages['loadingDialog.generationStep.searching'],
    generationStepMessages['loadingDialog.generationStep.selecting'],
    generationStepMessages['loadingDialog.generationStep.refining'],
];

/**
 * The reveal runs one tick past the last step so that step gets a working spell
 * of its own — without it the last step would arrive already checked off. This
 * tick is the floor on how long the sequence runs rather than the finish line:
 * the plan isn't complete until the requests behind the loader have landed too.
 */
export const GENERATION_SEQUENCE_TICK_COUNT = GENERATION_STEPS.length + 1;

/**
 * How long the tick that lands on `revealedStepCount` waits before firing. The
 * sequence opens quickly, settles into its interval while the list builds, then
 * holds on the last step for a shorter beat than a full interval.
 */
const getTickDelayMs = (revealedStepCount: number) => {
    if (revealedStepCount === 0) {
        return FIRST_GENERATION_STEP_DELAY_MS;
    }

    if (revealedStepCount >= GENERATION_STEPS.length) {
        return LAST_GENERATION_STEP_HOLD_MS;
    }

    return GENERATION_STEP_INTERVAL_MS;
};

/**
 * Reserved for the icon above the heading. The dots and the success check are
 * different sizes, so the slot holds the taller of the two and the swap at the
 * end of the sequence doesn't nudge the heading and steps below it.
 */
const TOP_ICON_SIZE = 48;

/** Shared by a step's spinner and its checkmark so neither shifts the row. */
const STEP_ICON_SIZE = 24;

/**
 * The width the step list is centered at — four grid columns, wide enough for
 * the longest step to sit on one line and narrow enough that the list still
 * reads as a group under the heading rather than spanning the whole dialog.
 */
const GENERATION_STEPS_WIDTH = maxWidths[4];

const styleDialogContent = {
    height: '100%',
};

const styleTopIcon = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: TOP_ICON_SIZE,
};

const styleSuccessCheck = {
    width: TOP_ICON_SIZE,
    height: TOP_ICON_SIZE,
};

const styleNewLines = (shouldShowQuote?: boolean) => ({
    whiteSpace: 'pre-line',
    ...(shouldShowQuote ? { color: 'text.body' } : {}),
});

const getContentHeadingStyles = {
    my: 4,
    pt: 2,
};

/*
 ** Everything above the list is centered by an ancestor that shrinks to fit its
 ** widest child, so a list sized by its own text can't sit still or sit center:
 ** it follows whatever the longest revealed step happens to be, which is what
 ** slid the steps left when the long third one joined. A definite width is what
 ** breaks that — the list stops taking its width from the text, the shrink-to-fit
 ** ancestor sizes itself around the list instead, and since that ancestor is the
 ** thing being centered the list lands centered on the page and each row centers
 ** inside it. It still gives way on screens too narrow for it.
 */
const styleGenerationSteps = {
    width: GENERATION_STEPS_WIDTH,
    maxWidth: '100%',
    alignItems: 'center',
    mt: 4,
    gap: 2,
};

const styleGenerationStep = {
    gap: 1.5,
    mb: 8,
};

const styleStepSpinner = {
    flexShrink: 0,
};

const styleStepCheck = {
    flexShrink: 0,
    fontSize: STEP_ICON_SIZE,
};

export const LoadingDialogStepContent = ({
    titleId,
    title,
    subtitle,
    titleVariant = 'h4',
    boxStyles,
    headingStyles,
    isReady = true,
    isConciergeLoadingDialog = false,
    isGenerationPending = false,
    onGenerationComplete,
}: LoadingDialogContentProps) => {
    const { formatMessage } = useIntl();
    const [revealedStepCount, setRevealedStepCount] = useState(0);
    const hasReportedCompleteRef = useRef(false);
    const onGenerationCompleteRef = useRef(onGenerationComplete);

    useEffect(() => {
        onGenerationCompleteRef.current = onGenerationComplete;
    }, [onGenerationComplete]);

    /*
     ** The dialog keeps this content mounted between opens, so start the
     ** sequence over on close rather than letting the next open pick up on a
     ** fully-revealed list.
     */
    useEffect(() => {
        if (!isReady) {
            setRevealedStepCount(0);
            hasReportedCompleteRef.current = false;
        }
    }, [isReady]);

    /*
     ** Reveal one more step per tick, then tick once more to close out the timed
     ** part of the sequence. Each tick chains the next timeout — with its own
     ** delay, since the opening and the closing hold are shorter than the
     ** interval between steps — instead of running a single interval, so the
     ** timer stops on its own once the sequence is done and everything stays put
     ** from there.
     */
    useEffect(() => {
        if (!isReady || revealedStepCount >= GENERATION_SEQUENCE_TICK_COUNT) {
            return undefined;
        }

        const timeoutId = setTimeout(() => {
            setRevealedStepCount((count) => count + 1);
        }, getTickDelayMs(revealedStepCount));

        return () => clearTimeout(timeoutId);
    }, [isReady, revealedStepCount]);

    /*
     ** The timed sequence sets the floor and the requests behind the loader set
     ** the finish, so whichever is slower is what the plan waits on. Once the
     ** steps have run out the last one keeps spinning for as long as the
     ** conversation and recommendation saves are still in flight, however long
     ** that takes, rather than checking off a plan that isn't there yet.
     */
    const isGenerationComplete =
        revealedStepCount >= GENERATION_SEQUENCE_TICK_COUNT &&
        !isGenerationPending;

    /*
     ** Only the newest step is still working — the ones above it have already
     ** checked off — and once the plan is complete nothing is left working, so
     ** the last step checks off too. The count runs one past the last step, so
     ** it's clamped here to keep the last step working through that final tick
     ** and for however long the plan stays pending after it.
     */
    const activeStepIndex = isGenerationComplete
        ? -1
        : Math.min(revealedStepCount, GENERATION_STEPS.length) - 1;

    /*
     ** Hand the finish back to whoever opened the loader — it has no way to know
     ** how long the sequence runs, and the dialog doesn't close itself. The
     ** checked-off list is a beat of the sequence rather than the end of it, so
     ** the handoff waits out the hold: the last step's spinner becomes a check,
     ** the success animation plays, and only then does the screen behind the
     ** loader move on. Without it the check lands on the same frame as the
     ** transition and the sequence reads as ending mid-work. The hold starts
     ** whenever the plan actually completes, which is the last tick or the
     ** moment the saves land, whichever the loader was still waiting on.
     **
     ** Reported once per open, since the reset above is what arms it again. The
     ** callback is read through a ref so a consumer re-rendering with a fresh
     ** one mid-hold doesn't restart the hold.
     */
    useEffect(() => {
        if (!isGenerationComplete || hasReportedCompleteRef.current) {
            return undefined;
        }

        const timeoutId = setTimeout(() => {
            hasReportedCompleteRef.current = true;
            onGenerationCompleteRef.current?.();
        }, GENERATION_COMPLETE_HOLD_MS);

        return () => clearTimeout(timeoutId);
    }, [isGenerationComplete]);

    // Lazily load the lottie animations since lottie is huge
    return (
        <Stack
            direction="column"
            alignItems="center"
            sx={sxAppend(styleDialogContent, boxStyles)}
        >
            {isReady && (
                <Box sx={styleTopIcon}>
                    {isGenerationComplete ? (
                        <Box
                            sx={styleSuccessCheck}
                            data-testid="GenerationStepsComplete"
                        >
                            <SuccessCheckAnimation />
                        </Box>
                    ) : (
                        <LoadingDots loop />
                    )}
                </Box>
            )}
            <Heading
                title={title}
                titleAlign="center"
                titleVariant={titleVariant}
                titleComponent="h2"
                titleId={titleId}
                subtitle={subtitle}
                subtitleAlign="center"
                subtitleVariant="body1"
                subtitleStyle={styleNewLines(isConciergeLoadingDialog)}
                sx={sxAppend(getContentHeadingStyles, headingStyles)}
            />
            {/* Announces each step as it joins the list rather than re-reading the whole list */}
            <Stack aria-live="polite" sx={styleGenerationSteps}>
                {GENERATION_STEPS.slice(0, revealedStepCount).map(
                    (step, index) => (
                        <Stack
                            key={step.id}
                            direction="row"
                            alignItems="flex-start"
                            sx={styleGenerationStep}
                        >
                            {/*
                             ** The icons are decorative: the step text carries
                             ** the meaning, and keeping them out of the live
                             ** region stops the spinner-to-check swap from
                             ** re-announcing a step that was already read out.
                             */}
                            {index === activeStepIndex ? (
                                <CircularProgress
                                    size={STEP_ICON_SIZE}
                                    color="info"
                                    sx={styleStepSpinner}
                                    aria-hidden
                                    data-testid="GenerationStepInProgress"
                                />
                            ) : (
                                <CheckmarkCircleOutline
                                    color="info"
                                    sx={styleStepCheck}
                                    data-testid="GenerationStepCompleted"
                                />
                            )}
                            <Typography variant="body1">
                                {formatMessage(step)}
                            </Typography>
                        </Stack>
                    )
                )}
            </Stack>
        </Stack>
    );
};
